import i18n from '@/i18n';
import { DataTableHeader } from 'vuetify';
import { AggregationTranslation } from '@/types/virtual-search/filters';
import { formatAdditionalTableHeaders } from '@/helpers/virtual-search/tableHeaders';
import { AirMblEntity } from '@/types/air/mbl/air-mbl.entity';
import { VIRTUAL_SORT_OPTIONS } from '@/types/virtual-search';

export const MawbTableHeaders: DataTableHeader[] = [
  {
    text: i18n.tc('ManifestNo'),
    value: 'shipmentNumber',
  },
  { text: i18n.tc('MAWB'), value: 'mawb' },
  { text: i18n.tc('Customer'), value: 'customer.accountName' },
  { text: i18n.tc('FreightForwarder'), value: 'freightForwarder.accountName' },
  { text: i18n.tc('Status'), value: 'status' },
  { text: i18n.tc('AirportOfDeparture'), value: 'pod.name' },
  { text: i18n.tc('AirportOfArrival'), value: 'pol.description' },
  { text: i18n.tc('ETA'), value: 'eta' },
].map((h) => ({ ...h, active: true, selected: true }));

export const AirFilterTranslation = (): Partial<AggregationTranslation<AirMblEntity>> => ({
  'destinationWarehouse.accountName.keyword': {
    type: 'select',
    label: 'Destination Warehouse',
    value: 'destinationWarehouse.accountName.keyword',
  },
  'status.keyword': {
    type: 'select',
    label: 'Status',
    value: 'status.keyword',
  },
  isReady: {
    type: 'boolean',
    label: 'Ready',
    value: 'isReady',
  },
  'fileSource.keyword': {
    type: 'select',
    label: 'File Source',
    value: 'fileSource.keyword',
  },
  isViewed: {
    type: 'boolean',
    label: 'Viewed',
    value: 'isViewed',
  },
  isFinalized: {
    type: 'boolean',
    label: 'Finalized',
    value: 'isFinalized',
  },
  'warehouse.accountName.keyword': {
    type: 'select',
    label: 'Warehouse',
    value: 'warehouse.accountName.keyword',
  },
  isPttRequired: {
    type: 'boolean',
    label: 'PTT Required',
    value: 'isPttRequired',
  },
  hazmat: {
    type: 'boolean',
    label: 'Hazmat',
    value: 'hazmat',
  },
  'emptyReturnLocation.accountName.keyword': {
    type: 'select',
    label: 'Empty Return Location',
    value: 'emptyReturnLocation.accountName.keyword',
  },
  'carrier.accountName.keyword': {
    type: 'select',
    label: 'Carrier',
    value: 'carrier.accountName.keyword',
  },
  headLoad: {
    type: 'boolean',
    label: 'Head Load',
    value: 'headLoad',
  },
  customerCommissionProcessed: {
    type: 'boolean',
    label: 'Customer Commission Processed',
    value: 'customerCommissionProcessed',
  },
  'service.description.keyword': {
    type: 'select',
    label: 'Service',
    value: 'service.description.keyword',
  },
  'paymentLookup.description.keyword': {
    type: 'select',
    label: 'Payment Lookup',
    value: 'paymentLookup.description.keyword',
  },
  'pod.description.keyword': {
    type: 'select',
    label: 'POD',
    value: 'pod.description.keyword',
  },
  'loadType.keyword': {
    type: 'select',
    label: 'Load Type',
    value: 'loadType.keyword',
  },
  isIpiReceiveLoad: {
    type: 'boolean',
    label: 'IPI Receive',
    value: 'isIpiReceiveLoad',
  },
  'pol.description.keyword': {
    type: 'select',
    label: 'POL',
    value: 'pol.description.keyword',
  },
  isClosed: {
    type: 'boolean',
    label: 'Closed',
    value: 'isClosed',
  },
  'pickupCarrier.accountName.keyword': {
    type: 'select',
    label: 'Pickup Carrier',
    value: 'pickupCarrier.accountName.keyword',
  },
  'customer.accountName.keyword': {
    type: 'select',
    label: 'Customer',
    value: 'customer.accountName.keyword',
  },
});

export const AirFilterAdditionalOption = (): Partial<AggregationTranslation<AirMblEntity>> => ({
  customerReference: {
    type: 'text',
    label: 'Customer Reference #',
    value: 'customerReference',
  },
  mawb: {
    type: 'text',
    label: 'MAWB #',
    value: 'mawb',
  },
  mib: {
    type: 'text',
    label: 'MIB #',
    value: 'mib',
  },
  shipmentNumber: {
    type: 'text',
    label: 'Shipment #',
    value: 'shipmentNumber',
  },
  door: {
    type: 'text',
    label: 'Door #',
    value: 'door',
  },
  deliveryNotes: {
    type: 'text',
    label: 'Delivery Notes',
    value: 'deliveryNotes',
  },
  strippedBy: {
    type: 'text',
    label: 'Stripped By',
    value: 'strippedBy',
  },
  cfsLocation: {
    type: 'text',
    label: 'CFS Location',
    value: 'cfsLocation',
  },
  cargoDescription: {
    type: 'text',
    label: 'Cargo Description',
    value: 'cargoDescription',
  },
  voyage: {
    type: 'text',
    label: 'Voyage',
    value: 'voyage',
  },
  eta: {
    type: 'date',
    label: 'ETA',
    value: 'eta',
  },
  dischargeDate: {
    type: 'date',
    label: 'Discharge Date',
    value: 'dischargeDate',
  },
  stripDate: {
    type: 'date',
    label: 'Strip Date',
    value: 'stripDate',
  },
  pttRequiredDate: {
    type: 'date',
    label: 'PTT Submitted Date',
    value: 'pttRequiredDate',
  },
  pttApprovalDate: {
    type: 'date',
    label: 'PTT Approved Date',
    value: 'pttApprovalDate',
  },
});

export const AirFilterDefaultForm = () => ({
  'status.keyword': [],
});

export const AirHblListFilterDefaultForm = () => ({
  'status.keyword': [],
  sortBy: { name: 'shipmentNumber', order: VIRTUAL_SORT_OPTIONS.ASC },
});

export const AdditionalMblTableHeaders: DataTableHeader[] = formatAdditionalTableHeaders(
  {
    ...AirFilterTranslation(),
    ...AirFilterAdditionalOption(),
  },
  MawbTableHeaders
);

export const ExtendedMawbTableHeaders: (DataTableHeader & {
  showColumnsMenu?: boolean;
  active?: boolean;
  selected?: boolean;
})[] = [
  ...MawbTableHeaders,
  ...AdditionalMblTableHeaders,
  {
    text: '',
    value: 'actions',
    showColumnsMenu: true,
    cellClass: '!sticky !right-0 !top-[15px] z-[2]',
    class: 'right-0',
    width: '50px',
    active: true,
    selected: true,
  },
];
